import {
  Chip,
  Stack,
  Button,
  Checkbox,
  TextField,
  Tooltip,
  Typography,
  LinearProgress,
  FormControlLabel,
  CircularProgress,
  type LinearProgressProps
} from "@material-ui/core";
import { Error, Warning, Check, Pause, UnfoldMore, UnfoldLess } from "@material-ui/icons";
import { BulkMessageDialogTrialBanner } from "./BulkMessageDialogTrialBanner";
import { useTranslation } from "react-i18next";
import { FilePreview } from "../../../../../@types/listings/fileData";
import { BulkMessageDialogTechnicalTipBanner } from "./BulkMessageDialogTechnicalTipBanner";
import { BulkMessageDialogContactPreview } from "./BulkMessageDialogContactPreview";
import { getFilteredSelectedContactsByFilterInput } from "utils/contacts/bulkMessage/helper";
import type { ChangeEvent, Dispatch, RefObject, SetStateAction } from "react";
import type { ProcessedListingData } from "../../../../../@types/sharedSchema";
import type { BulkMessageRecipient } from "utils/contacts/bulkMessage/new";
import {
  formatMinutesSeconds,
  type DelayStatus,
  type ActiveFieldTemplate,
  type SelectedMessageStatus,
  type BulkMessageTemplateType,
  type PlaceholderTemplateForm,
  type PerContactData,
  type CustomTemplateField
} from "utils/contacts/bulkMessage/new";
import Label, { type LabelColor } from "components/Label";
import { useMemo, useState } from "react";
import ForwardSendIcon from "../../../../ForwardSendIcon";

export type UsedParam = { id: string; label: string; color: LabelColor };

type BulkMessageDialogSendMessageStepProps = {
  listing: ProcessedListingData | null;
  selected: string[];
  autoScroll: boolean;
  filterInput: string;
  messageInput: string;
  filesPreview: FilePreview[];
  sendingMessage: boolean;
  showTrialBanner: boolean;
  delayStatus: DelayStatus;
  minisiteLinks: Record<string, string>;
  loadingMinisiteLinks: boolean;
  bulkMessageType: BulkMessageTemplateType;
  selectedMessageStatus: SelectedMessageStatus;
  messagePreviewListRef: RefObject<HTMLDivElement>;
  placeholderTemplatesForm: PlaceholderTemplateForm;
  setAutoScroll: Dispatch<SetStateAction<boolean>>;
  setFilterInput: Dispatch<SetStateAction<string>>;
  setSelectedMessageStatus: Dispatch<SetStateAction<SelectedMessageStatus>>;
  handleRetrySendingSingleBulkMessage: (contactId: string) => () => Promise<void>;
  perContactData?: PerContactData;
  expandAllPreviews?: boolean;
  setExpandAllPreviews?: Dispatch<SetStateAction<boolean | undefined>>;
  expanded?: boolean;
  activeTemplates?: ActiveFieldTemplate;
  customTemplateFields?: CustomTemplateField[];
};

export function BulkMessageDialogSendMessageStep({
  listing,
  selected,
  autoScroll,
  filterInput,
  messageInput,
  filesPreview,
  sendingMessage,
  showTrialBanner,
  delayStatus,
  minisiteLinks,
  loadingMinisiteLinks,
  bulkMessageType,
  messagePreviewListRef,
  selectedMessageStatus,
  placeholderTemplatesForm,
  setAutoScroll,
  setFilterInput,
  setSelectedMessageStatus,
  handleRetrySendingSingleBulkMessage,
  perContactData,
  expandAllPreviews,
  setExpandAllPreviews,
  expanded,
  activeTemplates,
  customTemplateFields
}: BulkMessageDialogSendMessageStepProps): JSX.Element {
  const { t: translate } = useTranslation();

  const handleChangeFilterInput = (event: ChangeEvent<HTMLInputElement>): void => {
    setFilterInput(event.target.value);
  };

  const handleToggleAutoScroll = (): void => {
    setAutoScroll(!autoScroll);
  };

  const mappedContacts: BulkMessageRecipient[] = Object.values(selectedMessageStatus).map(
    ({ data }) => data
  );

  const PARAM_COLORS: LabelColor[] = ["primary", "info", "success", "warning", "secondary"];

  // Compute which template params are actually used in the message text
  const usedParamsInMessage = useMemo(() => {
    if (!activeTemplates) return [];
    return Object.values(activeTemplates)
      .filter(({ id }) => messageInput.includes(`{{${id}}}`))
      .map(({ id, type }, i) => {
        const customField = customTemplateFields?.find((f) => f.id === id);
        const label = customField?.label ?? translate(`contactsPage.bulkMessage.templates.${type}.fields.${id}`);
        return { id, label, color: PARAM_COLORS[i % PARAM_COLORS.length] };
      });
  }, [activeTemplates, messageInput, customTemplateFields, translate]);

  const [showOnlyMissingParams, setShowOnlyMissingParams] = useState(false);

  // Check which contacts have missing param values
  const contactsWithMissingParams = useMemo(() => {
    if (!usedParamsInMessage.length) return new Set<string>();
    const missing = new Set<string>();
    for (const contact of mappedContacts) {
      const contactPerData = perContactData?.[contact.id];
      for (const { id } of usedParamsInMessage) {
        // Check minisite link availability
        if (id === "minisiteLink") {
          if (minisiteLinks[contact.id]) continue;
          missing.add(contact.id);
          break;
        }
        const hasValue =
          contactPerData?.[id] ||
          contact?.[id] ||
          (listing && (() => {
            const isAddr = ["street", "neighborhood", "city"].includes(id);
            if (isAddr) return listing.address?.[id];
            return listing[id];
          })());
        if (!hasValue) {
          missing.add(contact.id);
          break;
        }
      }
    }
    return missing;
  }, [usedParamsInMessage, mappedContacts, perContactData, listing, minisiteLinks]);

  const missingCount = contactsWithMissingParams.size;

  const textFilteredContacts = getFilteredSelectedContactsByFilterInput(
    mappedContacts,
    filterInput
  );

  const filteredMappedContacts = showOnlyMissingParams
    ? textFilteredContacts.filter((c) => contactsWithMissingParams.has(c.id))
    : textFilteredContacts;

  return (
    <Stack sx={{ height: "100%" }}>
      {showTrialBanner && <BulkMessageDialogTrialBanner />}
      <BulkMessageDialogTechnicalTipBanner />
      <Stack gap={2}>
        <Stack
          gap={2}
          flexWrap="wrap"
          direction="row"
          alignItems="center"
          justifyContent={{
            xs: "center",
            sm: "space-between"
          }}
        >
          <Typography variant="body1">
            {translate("contactsPage.bulkMessage.messagePreviewToContacts", {
              contactsCount: selected.length
            })}
          </Typography>
          <Stack sx={{ justifySelf: "end" }} direction="row" gap={2} alignItems="center">
            <TextField
              size="small"
              value={filterInput}
              label={"Search"}
              onChange={handleChangeFilterInput}
            />
            <FormControlLabel
              label={translate("contactsPage.control.autoScroll")}
              control={
                <Checkbox size="small" checked={autoScroll} onClick={handleToggleAutoScroll} />
              }
            />
          </Stack>
        </Stack>

        {/* Parameter chips (only those actually used in message) + expand/collapse toggle */}
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          {usedParamsInMessage.map(({ id, label, color }) => (
            <Label key={id} variant="ghost" color={color} sx={{ fontSize: 11 }}>
              {`{{${id}}} = ${label}`}
            </Label>
          ))}
          <Stack direction="row" gap={1} alignItems="center" sx={{ ml: "auto" }}>
            {missingCount > 0 && (
              <Button
                size="small"
                variant={showOnlyMissingParams ? "contained" : "outlined"}
                color="warning"
                onClick={() => setShowOnlyMissingParams((prev) => !prev)}
              >
                {translate("contactsPage.bulkMessage.showMissingOnly", { count: missingCount })}
              </Button>
            )}
            {setExpandAllPreviews && (
              <Button
                size="small"
                variant="text"
                startIcon={expandAllPreviews ? <UnfoldLess /> : <UnfoldMore />}
                onClick={() => setExpandAllPreviews((prev) => !prev)}
              >
                {expandAllPreviews
                  ? translate("contactsPage.bulkMessage.collapseAll")
                  : translate("contactsPage.bulkMessage.expandAll")}
              </Button>
            )}
          </Stack>
        </Stack>

        <Stack
          ref={messagePreviewListRef}
          sx={{
            maxHeight: expanded ? "none" : 240,
            overflowY: "auto"
          }}
        >
          {loadingMinisiteLinks && (
            <Stack direction="row" gap={1} alignItems="center" justifyContent="center" sx={{ py: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                {translate("contactsPage.bulkMessage.loadingMinisiteLinks")}
              </Typography>
            </Stack>
          )}
          {filteredMappedContacts.map((contact, index) => (
            <BulkMessageDialogContactPreview
              index={index}
              listing={listing}
              contact={contact}
              message={messageInput}
              filesPreview={filesPreview}
              sendingMessage={sendingMessage}
              bulkMessageType={bulkMessageType}
              minisiteLink={minisiteLinks[contact.id]}
              loadingMinisiteLinks={loadingMinisiteLinks}
              selectedMessageStatus={selectedMessageStatus}
              placeholderTemplatesForm={placeholderTemplatesForm}
              perContactData={perContactData?.[contact.id]}
              usedParamsInMessage={usedParamsInMessage}
              expandAllOverride={expandAllPreviews}
              setSelectedMessageStatus={setSelectedMessageStatus}
              handleRetrySendingSingleBulkMessage={handleRetrySendingSingleBulkMessage}
              key={contact.id}
            />
          ))}
        </Stack>
        {/* Always reserve this row's height so the countdown indicator toggling
            on/off doesn't change content height and toggle the dialog scrollbar
            (which reflows content width → rapid scrollbar flicker/jumps). */}
        <Stack
          direction="row"
          gap={1}
          alignItems="center"
          justifyContent="center"
          sx={{ mt: 1, minHeight: 24 }}
        >
          {delayStatus.type === "waiting" && (
            <>
              <CircularProgress size={16} color="warning" />
              <Typography variant="caption" color="warning.main" sx={{ fontWeight: 600 }}>
                {`השליחה מושהית עד ש-WhatsApp Web יחזור. אף הודעה לא מדולגת. ${delayStatus.message ?? ""}`}
              </Typography>
            </>
          )}
          {delayStatus.type === "stopped" && (
            <Typography variant="caption" color="error" sx={{ fontWeight: 600 }}>
              {delayStatus.message}
            </Typography>
          )}
          {(delayStatus.type === "delaying" || delayStatus.type === "cooldown") && (
            <>
              <CircularProgress size={16} color="info" />
              <Typography variant="caption" color="textSecondary">
                {delayStatus.type === "cooldown"
                  ? translate("contactsPage.bulkMessage.delaySettings.cooldownWaiting", {
                      time: formatMinutesSeconds(delayStatus.remainingSeconds)
                    })
                  : translate("contactsPage.bulkMessage.delaySettings.delayWaiting", {
                      seconds: delayStatus.remainingSeconds
                    })}
              </Typography>
            </>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}

type BulkMessageDialogSendMessageStepActionsProps = {
  selected: string[];
  sendingSingleMessage: boolean;
  agreeToUserAgreement: boolean;
  sendingProgressCount: number;
  sendingMultipleMessage: boolean;
  selectedMessageStatus: SelectedMessageStatus;
  setSendingMultipleMessage: Dispatch<SetStateAction<boolean>>;
  setAgreeToUserAgreement: Dispatch<SetStateAction<boolean>>;
  handleBackStep: () => void;
};

export function BulkMessageDialogSendMessageStepActions({
  selected,
  sendingSingleMessage,
  agreeToUserAgreement,
  sendingProgressCount,
  sendingMultipleMessage,
  selectedMessageStatus,
  setAgreeToUserAgreement,
  setSendingMultipleMessage,
  handleBackStep
}: BulkMessageDialogSendMessageStepActionsProps): JSX.Element {

  const { t: translate } = useTranslation();

  const handleToggleSendingMessage = (state: boolean) => (): void => {
    setSendingMultipleMessage(state);
  };

  const handleToggleAgreeToUserAgreement = (): void => {
    setAgreeToUserAgreement(!agreeToUserAgreement);
  };

  const isAllSuccessfullySent = Object.values(selectedMessageStatus).every(({ status }) =>
    ["success", "skipped"].includes(status)
  );

  const isBackDisabled = sendingSingleMessage || sendingMultipleMessage;
  const isSendDisabled = sendingSingleMessage || isAllSuccessfullySent || !agreeToUserAgreement;


  return (
    <Stack sx={{ width: "100%" }}>
      <BulkMessageDialogSendingProgressBar
        selected={selected}
        sendingProgressCount={sendingProgressCount}
        sendingSingleMessage={sendingSingleMessage}
        selectedMessageStatus={selectedMessageStatus}
        sendingMultipleMessage={sendingMultipleMessage}
      />
      <Stack gap={2}>
        <FormControlLabel
          sx={{ mx: "auto" }}
          label={translate("contactsPage.bulkMessage.userAgreement")}
          control={
            <Checkbox
              checked={agreeToUserAgreement}
              disabled={isBackDisabled}
              onClick={handleToggleAgreeToUserAgreement}
            />
          }
        />
        <Stack direction="row" gap={2} justifyContent="space-between">
          <Button variant="outlined" disabled={isBackDisabled} onClick={handleBackStep}>
            {translate("contactsPage.control.back")}
          </Button>
          {sendingMultipleMessage ? (
            <Button
              sx={{ color: "white" }}
              color="warning"
              variant="contained"
              disabled={isSendDisabled}
              startIcon={<Pause />}
              onClick={handleToggleSendingMessage(false)}
            >
              {translate("contactsPage.control.pause")}
            </Button>
          ) : (
            <Button
              endIcon={<ForwardSendIcon />}
              color="primary"
              variant="contained"
              disabled={isSendDisabled}
              onClick={handleToggleSendingMessage(true)}
            >
              {translate("contactsPage.control.send")}
            </Button>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}

type BulkMessageDialogSendingProgressBarProps = {
  selected: string[];
  sendingProgressCount: number;
  selectedMessageStatus: SelectedMessageStatus;
  sendingSingleMessage: boolean;
  sendingMultipleMessage: boolean;
};

function BulkMessageDialogSendingProgressBar({
  selected,
  sendingProgressCount,
  sendingSingleMessage,
  selectedMessageStatus,
  sendingMultipleMessage
}: BulkMessageDialogSendingProgressBarProps): JSX.Element {
  const { t: translate } = useTranslation();

  const progress = (sendingProgressCount / selected.length) * 100;
  const formattedProgress = `${Math.round(progress)}%`;

  const errorMessageCount = Object.values(selectedMessageStatus).filter(
    ({ status }) => status === "error"
  );

  const warningMessageCount = Object.values(selectedMessageStatus).filter(
    ({ status }) => status === "warning"
  );

  const successMessageSentCount = Object.values(selectedMessageStatus).filter(
    ({ status }) => status === "success"
  );

  const isAllSuccessfullySent = successMessageSentCount.length === selected.length;

  let color: LinearProgressProps["color"] = "primary";

  if (errorMessageCount.length) {
    color = "error";
  } else if (warningMessageCount.length) {
    color = "warning";
  } else if (isAllSuccessfullySent) {
    color = "success";
  }

  return (
    <Stack sx={{ mt: "auto", mx: 4 }} gap={1}>
      <Stack direction="row" gap={2} alignItems="center">
        {sendingSingleMessage ? (
          <CircularProgress color="info" size={24} />
        ) : (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {formattedProgress}
          </Typography>
        )}
        <LinearProgress
          sx={{ width: "100%" }}
          value={progress}
          color={color}
          variant="determinate"
        />
      </Stack>
      <Stack direction="row" gap={1} justifyContent="center">
        {!!successMessageSentCount.length && (
          <Chip
            sx={{ color: "white" }}
            size="small"
            icon={<Check />}
            label={translate("contactsPage.bulkMessage.progressStatus.success", {
              count: successMessageSentCount.length
            })}
            color="success"
          />
        )}
        {!!warningMessageCount.length && (
          <Chip
            sx={{ color: "white" }}
            size="small"
            icon={<Warning />}
            label={translate("contactsPage.bulkMessage.progressStatus.warning", {
              count: warningMessageCount.length
            })}
            color="warning"
          />
        )}
        {!!errorMessageCount.length && (
          <Chip
            sx={{ color: "white" }}
            size="small"
            icon={<Error />}
            label={translate("contactsPage.bulkMessage.progressStatus.error", {
              count: errorMessageCount.length
            })}
            color="error"
          />
        )}
      </Stack>
    </Stack>
  );
}
